import React, { useState, useEffect , useMemo, useCallback} from 'react';
import { useNavigate } from 'react-router-dom';
import './Main.css'; 
import SideBar from '../Components/SideBar';
import { API_BASE_URL } from '../config.js';
import MainMobile from './Main_mobile';
export default function Main() {
  const [patients, setPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState(""); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sortOrder, setSortOrder] = useState("newest");
  const [genderFilter, setGenderFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMobileFilter, setShowMobileFilter] = useState(false);

  const navigate = useNavigate();
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const normalizeArabic = (text) => {
    if (!text) return "";
    return String(text)
      .replace(/[أإآ]/g, "ا")
      .replace(/ة/g, "ه")
      .replace(/ى/g, "ي")
      .replace(/[\u064B-\u065F]/g, "") // إزالة التشكيل إن وجد
      .toLowerCase()
      .trim();
  };

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/login');
        return;
      }
      const response = await fetch(`${ API_BASE_URL }/patients/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        }
      });
      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('access_token');
        navigate('/login');
        throw new Error("انتهت صلاحية الجلسة، يرجى تسجيل الدخول مجدداً");
      }
      if (!response.ok) throw new Error(`خطأ في السيرفر: ${response.status}`);
      
      const data = await response.json();
      // حل مشكلة الـ Pagination إن وجدت (قراءة data.results بدلاً من data مباشرة)
      const patientsArray = data.results ? data.results : (Array.isArray(data) ? data : []);
      setPatients(patientsArray);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  // 🔹 استخراج قائمة المدن الفريدة من البيانات المجلوبة (ديناميكياً)
  const availableCities = useMemo(() => {
    const cities = patients.map(p => p.city).filter(Boolean); // افتراض أن حقل المدينة اسمه city
    return [...new Set(cities)];
  }, [patients]);

  // 🔹 خوارزمية الفلترة والبحث والترتيب (مع حماية XSS وتحسين الأداء بـ useMemo)
 const filteredAndSortedPatients = useMemo(() => {
    let result = [...patients];
    
    // 1. تنظيف وتوحيد نص البحث
    const rawSearchTerm = searchTerm.replace(/[<>"']/g, '').trim();
    const cleanSearchTerm = normalizeArabic(rawSearchTerm);

    // 2. البحث (الفلترة)
    if (cleanSearchTerm) {
      result = result.filter(patient => {
        const patientName = normalizeArabic(patient.name);
        const patientNationalNum = String(patient.national_number || "");
        const patientPhone = String(patient.phone || ""); // البحث برقم الهاتف أيضاً
        
        return (
          patientName.includes(cleanSearchTerm) ||
          patientNationalNum.includes(rawSearchTerm) ||
          patientPhone.includes(rawSearchTerm)
        );
      });
    }

    // 3. فلتر الجنس
    if (genderFilter !== "all") {
      result = result.filter(patient => patient.gender === genderFilter);
    }

    // 4. فلتر المدينة
    if (cityFilter !== "all") {
      result = result.filter(patient => patient.city === cityFilter);
    }

    // 5. الترتيب
    result.sort((a, b) => {
      const timeA = new Date(a.created_at || a.id).getTime();
      const timeB = new Date(b.created_at || b.id).getTime();
      return sortOrder === "newest" ? timeB - timeA : timeA - timeB;
    });

    return result;
  }, [patients, searchTerm, genderFilter, cityFilter, sortOrder]);
  // 📱 إذا كان الجهاز موبايل، نقوم بإرجاع مكون الموبايل وتمرير البيانات كـ Props
  if (isMobile) {
    return (
      <MainMobile 
        patients={filteredAndSortedPatients} // نمرر البيانات المفلترة فقط
        totalPatientsCount={patients.length}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        loading={loading}
        error={error}
        isDatabaseEmpty={patients.length === 0}
        isSearchEmpty={filteredAndSortedPatients.length === 0 && patients.length > 0}
        onAddPatient={() => navigate('/add-patient')}
        onPatientClick={(id) => navigate(`/patient-profile/${id}`, { state: { role: "doctor" } })}
        onRefresh={fetchPatients}
        // Props الفلترة
        sortOrder={sortOrder} setSortOrder={setSortOrder}
        genderFilter={genderFilter} setGenderFilter={setGenderFilter}
        cityFilter={cityFilter} setCityFilter={setCityFilter}
        availableCities={availableCities}
        showMobileFilter={showMobileFilter}
        setShowMobileFilter={setShowMobileFilter}
      />
    );
  }

  return (
    <div className="dashboard-container" dir="rtl">
      <SideBar />
      <main className="main-content">
        <div className="bg-watermark"></div>
        <div className="content-wrapper">
          {/* شريط البحث والفلترة الديسكتوب (مبسط) */}
          <div className="desktop-controls" style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
            <div className="search-container" style={{ flex: 1, margin: 0 }}>
              <input 
                type="text" 
                className="search-input" 
                placeholder="ابحث بالاسم، الرقم الوطني، رقم الهاتف..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)} 
              />
              <i className="fa-solid fa-magnifying-glass search-icon"></i>
            </div>
            <select className="input" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
              <option value="newest">الأحدث أولاً</option>
              <option value="oldest">الأقدم أولاً</option>
            </select>
            <select className="input" value={genderFilter} onChange={(e) => setGenderFilter(e.target.value)}>
              <option value="all">الجنس (الكل)</option>
              <option value="male">ذكر</option>
              <option value="female">أنثى</option>
            </select>
          </div>

          {loading && <div className="status-message">جاري التحميل...</div>}
          
          {error && (
            <div className="no-patients-view">
              <h2 className="no-patients-text" style={{color: 'red'}}>حدث خطأ: {error}</h2>
            </div>
          )}
          
          {!loading && !error && (
            patients.length === 0 ? (
              <div className="no-patients-view">
                <h2 className="no-patients-text">لا توجد حالات بعد</h2>
                <button className="add-patient-inline-btn" onClick={() => navigate('/add-patient')}>اضافة مريض</button>
              </div>
            ) : filteredAndSortedPatients.length === 0 ? (
              <div className="no-patients-view">
                <h2 className="no-patients-text">لا توجد حالة مطابقة لخيارات البحث</h2>
              </div>
            ) : (
              <>
                <div className="header-section">
                  <h1 className="page-title">الحالات المُسجّلة ({filteredAndSortedPatients.length})</h1>
                  <button className="add-btn" onClick={() => navigate('/add-patient')}>+</button>
                </div>
                <div className="patients-list">
                  {filteredAndSortedPatients.map((patient) => (
                    <div key={patient.id} className="patient-card" onClick={() => navigate(`/patient-profile/${patient.id}`, { state: { role: "doctor" } })}>
                      <div className="patient-info">
                        <h3 className="patient-name">{patient.name}</h3>
                        <div className="patient-details">
                          <p className="patient-code">الرقم الوطني : {patient.national_number}</p>
                          <p className="patient-code">المدينة: {patient.city || 'غير محدد'}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )
          )}
        </div>
      </main>
    </div>
  );
}